import { OdaListesi } from './OdaListesi';
/**
 * Oda tipi şeridi (şartname 6): satırın altında tam genişlikte açılır (akordiyon değil, birden çok şerit açık olabilir).
 * Her oda grubu bir sütun; seçenek kartı = radyo. Klavye: açılınca odak ilk radyoya, ok tuşları grup içinde seçer
 * (yerel radyo davranışı), Tab gruplar arası, Escape kapatır (üst bileşen).
 */
import { useEffect, useRef, useState } from 'react';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { Ikon } from './Ikon';
import { kalanMetni, kalanMs, konukMetni, paraYaz, varsayilanaFark, varsayilanSecenekNo, type SatirHesabi } from './mantik';
import type { AlternatifDagilim, KapasiteUyumu, OdaGrubu, OdaSecenegi, OtelSonucu } from './tipler';

const KAPASITE: Readonly<Record<KapasiteUyumu, { readonly ikon: 'tik' | 'yatak' | 'kapat' | 'bilgi'; readonly metin: string; readonly ton: string }>> = {
  UYGUN: { ikon: 'tik', metin: M.uygun, ton: 'ok' },
  EK_YATAKLI: { ikon: 'yatak', metin: M.ekYatak, ton: 'uyari' },
  UYMUYOR: { ikon: 'kapat', metin: M.uymuyor, ton: 'tehlike' },
  BILINMIYOR: { ikon: 'bilgi', metin: M.kapasiteBilinmiyor, ton: 'notr' },
};

function iptalMetni(s: OdaSecenegi): string {
  if (s.iptal.iadeEdilebilir === true && s.iptal.sonTarih !== null) return M.iptalUcretsiz(new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'short', timeZone: 'Asia/Dubai' }).format(new Date(s.iptal.sonTarih)));
  if (s.iptal.iadeEdilebilir === false) return M.iptalYok;
  return M.iptalBilinmiyor;
}

export function OdaTipiSeridi({
  kimlik,
  otel,
  sonuc,
  hesap,
  devreDisi,
  secimDegisti,
  hepsineUygula,
  varsayilanaDon,
  kapat,
  dagilimUygula,
}: {
  kimlik: string;
  otel: string;
  sonuc: OtelSonucu;
  hesap: SatirHesabi;
  devreDisi: boolean;
  secimDegisti: (grupAnahtari: string, secenekNo: string) => void;
  hepsineUygula: (grupAnahtari: string) => void;
  varsayilanaDon: () => void;
  kapat: () => void;
  dagilimUygula: (grup: OdaGrubu, dagilim: AlternatifDagilim) => void;
}) {
  const [sifirlama, setSifirlama] = useState(0);
  const kap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = kap.current;
    (k?.querySelector<HTMLInputElement>('input[type="radio"]:checked:not(:disabled)') ?? k?.querySelector<HTMLInputElement>('input[type="radio"]:not(:disabled)'))?.focus();
  }, []);
  const toplam = hesap.toplamMinor !== null && hesap.paraBirimi !== null ? paraYaz(hesap.toplamMinor, hesap.paraBirimi) : M.ozetTamamlanmadi;
  const gruplar = sonuc.odaGruplari;
  return (
    <div className="ht-serit" id={kimlik} role="group" aria-label={M.seritBaslik(otel)} ref={kap} data-testid={`ht-serit-${kimlik}`}>
      <div className="ht-serit-ust">
        <h3>{M.seritBaslik(otel)}</h3>
        <span className="ht-serit-toplam">{M.seritToplam(toplam)}</span>
        <button type="button" className="ht-btn" onClick={() => { varsayilanaDon(); setSifirlama(n => n + 1); }} disabled={devreDisi}>{M.varsayilanaDon}</button>
        <button type="button" className="ht-btn" onClick={kapat}><Ikon ad="kapat" kucuk />{M.kapat}</button>
      </div>
      <p className="ht-ipucu">{M.seritAciklama}</p>
      <div className="ht-serit-izgara" style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(gruplar.length, 1), 3)}, minmax(0, 1fr))` }}>
        {gruplar.map((g) => {
          const secili = hesap.secilenler.find((x) => x.grup.anahtar === g.anahtar)?.secenek.secenekNo ?? null;
          const digerleri = gruplar.filter((x) => x.anahtar !== g.anahtar);
          const seciliSecenek = g.secenekler.find((s) => s.secenekNo === secili);
          const uygulanabilir = seciliSecenek !== undefined && digerleri.length > 0 && digerleri.every((d) => d.secenekler.some((s) => s.odaTipi === seciliSecenek.odaTipi && s.kapasite !== 'UYMUYOR'));
          const alt = sonuc.alternatifler.find((a) => a.grupAnahtari === g.anahtar)?.dagilimlar ?? [];
          return (
            <div className="ht-serit-oda" key={g.anahtar}>
              <div className="ht-serit-oda-ust">
                <b>{M.odaBasligi(g.odaNolari, konukMetni(g.duzen))}{g.adet > 1 ? ` (x${g.adet})` : ''}</b>
                {gruplar.length > 1 ? (
                  <button
                    type="button"
                    className="ht-baglanti"
                    aria-disabled={!uygulanabilir || devreDisi}
                    title={uygulanabilir ? undefined : M.hepsineUygulaKapali}
                    onClick={() => { if (uygulanabilir && !devreDisi) hepsineUygula(g.anahtar); }}
                  >{M.hepsineUygula}</button>
                ) : null}
              </div>
              {g.cocukNotlari.map((n) => <p className="ht-ipucu" key={n}>{n}</p>)}
              {g.cocukKuraliBilinmiyor ? <p className="ht-ipucu">{M.cocukKuralYok}</p> : null}
              {g.secenekler.length === 0 ? (
                <div className="ht-uyari-kutu">
                  <Ikon ad="uyari" />
                  <div>
                    <strong>{M.alternatifBaslik}</strong>
                    <p>{M.alternatifAcik(g.odaNolari[0] ?? 1)}</p>
                  </div>
                </div>
              ) : null}
              {alt.map((d, i) => (
                <div className="ht-alternatif" key={i}>
                  <span>{d.odalar.map(konukMetni).join(' + ')}</span>
                  {d.enUcuzToplamMinor !== null && d.paraBirimi !== null ? <small>{M.dagilimTahmin(paraYaz(d.enUcuzToplamMinor, d.paraBirimi))}</small> : null}
                  <small>{d.not}</small>
                  <button type="button" className="ht-btn" disabled={devreDisi} onClick={() => dagilimUygula(g, d)}>{M.dagilimiUygula}</button>
                </div>
              ))}
              <OdaListesi slug={sonuc.slug} secenekler={g.secenekler} secili={secili} sifirlama={sifirlama}>{(s) => {
                const kapasite = KAPASITE[s.kapasite];
                const kapali = devreDisi || s.kapasite === 'UYMUYOR';
                const acik = secili === s.secenekNo;
                const fark = varsayilanaFark(g, s);
                const bitis = kalanMs(s.quoteExpiresAt, Date.now());
                return (
                  <label key={s.secenekNo} className={`ht-secenek${acik ? ' ht-secenek-acik' : ''}${kapali ? ' ht-secenek-kapali' : ''}`}>
                    <input
                      type="radio"
                      name={`${kimlik}-${g.anahtar}`}
                      checked={acik}
                      disabled={kapali}
                      onChange={() => secimDegisti(g.anahtar, s.secenekNo)}
                      data-testid={`ht-secenek-${kimlik}-${g.anahtar}-${s.secenekNo}`}
                    />
                    <span className="ht-secenek-govde">
                      <span className="ht-secenek-satir">
                        <b>{s.odaTipi}</b>
                        <span className="ht-oda-fiyat"><small>{M.odaSecenekToplami(g.adet)}</small><span className="ht-fiyat">{paraYaz(s.toplamMinor, s.paraBirimi)}</span></span>
                      </span>
                      <span className="ht-secenek-satir ht-ipucu">
                        <span>{[s.alanM2 !== null ? M.alanM2(s.alanM2) : null, s.yatakDuzeni].filter((x) => x !== null).join(', ') || s.pansiyon}</span>
                        <span className={`ht-oda-fark ${fark === null || fark === 0 ? '' : fark > 0 ? 'ht-fark-art' : 'ht-fark-azal'}`}>
                          {s.secenekNo === varsayilanSecenekNo(g) ? M.varsayilan : fark === null ? '' : M.odaVarsayilanFarki(`${fark > 0 ? '+' : fark < 0 ? '-' : ''}${paraYaz(Math.abs(fark), s.paraBirimi)}`)}
                        </span>
                      </span>
                      <span className="ht-etiketler">
                        <span className={`ht-et ht-et-${kapasite.ton}`}><Ikon ad={kapasite.ikon} kucuk />{kapasite.metin}</span>
                        <span className="ht-et"><Ikon ad="fincan" kucuk />{M.pansiyon[s.kahvalti]}</span>
                        <span className="ht-et"><Ikon ad="kalkan" kucuk />{iptalMetni(s)}</span>
                        {bitis !== null && bitis > 0 && bitis <= 30 * 60_000 ? <span className="ht-et ht-et-uyari"><Ikon ad="saat" kucuk />{kalanMetni(bitis)}</span> : null}
                      </span>
                      {s.kapasiteNotu !== null ? <span className="ht-ipucu">{s.kapasiteNotu}</span> : null}
                    </span>
                  </label>
                );
              }}</OdaListesi>
            </div>
          );
        })}
      </div>
    </div>
  );
}
